import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { getSecurityControls } from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import BarChartIcon from '@mui/icons-material/BarChart'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { formatTitle } from '../../utils/formatTitle'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatNormLabel } from '../../utils/normLabels'
import { SecurityControls } from '../../types/securityControls'
import { ListPageShell, LIST_PAGE_COLORS } from '../../components/common/ListPageShell'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { onClickableTableRow } from '../../utils/tableRowClick'
import '../../components/common/ListPageTable.css'
import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import TablePager from '../../components/common/TablePager'
import { useClientPagination } from '../../hooks/useClientPagination'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import { writeSecurityNav } from '../../utils/securityNav'
import { formatDecimal } from '../../utils/formatNumbers'

const C = LIST_PAGE_COLORS

export default function SecurityControlsList() {
  const { SecurityId } = useParams<{ SecurityId: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()

  const [search, setSearch] = useState('')

  const selectedNorm = useSelector(selectSpecifications)[0]?.NormBase || ''
  const controls = useSelector(
    (state: RootState) => state.securitycheckFamily.SecurityControls
  )
  const loading = useSelector(
    (state: RootState) => state.securitycheckFamily.loading
  )
  const showLoader = useTableLoadingGate(loading, [SecurityId])
  const roles = useSelector((state: RootState) => state.auth.roles)

  useEffect(() => {
    if (SecurityId) dispatch(getSecurityControls(SecurityId))
  }, [dispatch, SecurityId])

  const familyLabel = useMemo(() => {
    const fromRow = controls?.[0]?.LabelSCF
    return fromRow ? formatTitle(fromRow) : ''
  }, [controls])

  useEffect(() => {
    if (!SecurityId) return
    writeSecurityNav({
      norm: selectedNorm,
      familyId: SecurityId,
      familyLabel: familyLabel || undefined,
    })
  }, [SecurityId, selectedNorm, familyLabel])

  const openControl = (controlId: string, controlLabel?: string) => {
    writeSecurityNav({
        norm: selectedNorm,
      familyId: SecurityId,
      familyLabel: familyLabel || undefined,
      controlId,
      controlLabel: controlLabel || undefined,
    })
    navigate(`/security-control-details/${controlId}`, {
      state: {
        familyId: SecurityId,
        familyLabel,
        controlLabel,
        norm: selectedNorm,
      },
    })
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const rows = controls || []
    if (!q) return rows
    return rows.filter((row: SecurityControls) => {
      const label = (row.Label || '').toLowerCase()
      const desc = (row.Description || '').toLowerCase()
      const source = (row.Source || '').toLowerCase()
      return label.includes(q) || desc.includes(q) || source.includes(q)
    })
  }, [controls, search])

  const pager = useClientPagination(filtered, 10)

  const normLabel = formatNormLabel(selectedNorm)
  const canAnalyze =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.DSSI) ||
    hasRole(roles, UserRole.ADMIN)

  return (
    <ListPageShell
      eyebrow={t('securityControls')}
      title={t('ISO security controls List', { norm: normLabel })}
      titleTourId="security-controls-page-header"
      subtitle={
        familyLabel
          ? t('listPages.securityControlsSubFamily', {
              family: familyLabel,
              norm: normLabel,
              defaultValue: `Controls in {{family}} for {{norm}}, with source and score.`,
            })
          : t('listPages.securityControlsSub', {
              norm: normLabel,
              defaultValue:
                'Security controls for this family, with source and score.',
            })
      }
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder={t('securityControlsPage.filter')}
      countLabel={t('listPages.controlsCount', {
        count: filtered.length,
      })}
      headActions={
        canAnalyze && SecurityId ? (
          <button
            type="button"
            onClick={() =>
              navigate(`/security-controls/view-analysis/${SecurityId}`)
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              height: 38,
              padding: '0 16px',
              borderRadius: 8,
              border: 'none',
              background: C.teal,
              color: '#fff',
              fontSize: 14.5,
              fontWeight: 500,
              fontFamily: 'Lato, sans-serif',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(13,155,135,0.35)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = C.tealDeep
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = C.teal
            }}
          >
            <BarChartIcon style={{ fontSize: 16 }} />
            {t('buttons.viewAnalysis')}
          </button>
        ) : undefined
      }
    >
      <div className="incyia-table-wrap">
        {showLoader ? (
          <TableLoadingState />
        ) : filtered.length === 0 ? (
          <TableEmptyState />
        ) : (
          <>
            <table className="incyia-table">
              <thead>
                <tr>
                  <th>{t('Label')}</th>
                  <th>{t('Description')}</th>
                  <th>{t('Source')}</th>
                  <th>{t('listPages.score')}</th>
                  <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((row: SecurityControls) => {
                  const id = row._id as string
                  const score =
                    row.SCScore != null && !Number.isNaN(Number(row.SCScore))
                      ? formatDecimal(Number(row.SCScore))
                      : '—'

                  return (
                    <tr key={id} onClick={onClickableTableRow}>
                      <td className="incyia-col-pinned">
                        <button
                          type="button"
                          className="incyia-name-link"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                          onClick={() =>
                            openControl(
                              id,
                              row.Label ? formatTitle(row.Label) : undefined
                            )
                          }
                        >
                          <ListPageDescription
                            value={
                              row.Label ? formatTitle(row.Label) : undefined
                            }
                            tone="primary"
                          />
                        </button>
                      </td>
                      <td className="incyia-wrap">
                        <ListPageDescription value={row.Description} />
                      </td>
                      <td className="incyia-muted">
                        <ListPageDescription value={row.Source} />
                      </td>
                      <td>
                        <span className="incyia-money">{score}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="incyia-row-actions incyia-row-actions--labeled">
                          <button
                            type="button"
                            className="incyia-row-action-btn"
                            title={t('Existing Controls')}
                            onClick={() => navigate(`/security-existing/${id}`)}
                          >
                            <span className="incyia-row-action-full">
                              {t('listPages.existingControlsAction', {
                                defaultValue: 'Existing controls',
                              })}
                            </span>
                            <span className="incyia-row-action-short">
                              {t('listPages.existingControlsShort', {
                                defaultValue: 'Existing',
                              })}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="incyia-row-action-btn"
                            title={t('SubDomains')}
                            onClick={() => navigate(`/security-subd/${id}`)}
                          >
                            <span className="incyia-row-action-full">
                              {t('listPages.subdomainsAction', {
                                defaultValue: 'Subdomains',
                              })}
                            </span>
                            <span className="incyia-row-action-short">
                              {t('listPages.subdomainsShort', {
                                defaultValue: 'Subdomains',
                              })}
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {pager.showPager ? (
              <TablePager
                page={pager.page}
                pageSize={pager.pageSize}
                total={pager.total}
                pageCount={pager.pageCount}
                from={pager.from}
                to={pager.to}
                pageSizeOptions={pager.pageSizeOptions}
                onPageChange={pager.setPage}
                onPageSizeChange={pager.setPageSize}
              />
            ) : null}
          </>
        )}
      </div>
    </ListPageShell>
  )
}
